import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fi } from 'date-fns/locale';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { DASHBOARD } from '@/constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Package, Scale, TrendingUp, ArrowRight, Truck, Award, ShoppingBag,
  AlertCircle, Calendar, Sprout, X,
} from 'lucide-react';

const fmtKg = (n) => `${Number(n || 0).toLocaleString('fi-FI')} kg`;
const fmtDay = (d) => format(parseISO(d), 'EEE d.M.', { locale: fi });

function KpiCard({ icon: Icon, label, value, hint, tone = 'green', testid }) {
  const tones = {
    green:  'from-[#2A4B30] to-[#3a6841] text-white',
    sand:   'from-[#E9D9B5] to-[#F0E4C7] text-[#2A4B30]',
    rust:   'from-[#C05A3C] to-[#D87555] text-white',
    cream:  'from-white to-[#F4F5F1] text-[#1C1C1A] border border-[#E5E6E3]',
  }[tone] || tones?.green;
  return (
    <div data-testid={testid} className={`rounded-2xl p-5 shadow-sm bg-gradient-to-br ${tones} relative overflow-hidden`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider opacity-80 font-medium">{label}</div>
          <div className="mt-2 text-3xl font-heading tracking-tight tabular-nums">{value}</div>
          {hint && <div className="mt-1 text-xs opacity-80">{hint}</div>}
        </div>
        <div className="opacity-70"><Icon size={28} strokeWidth={1.6} /></div>
      </div>
    </div>
  );
}

function WeekChart({ trend, onPick }) {
  const max = Math.max(1, ...trend.map((d) => d.tilaukset_kg));
  return (
    <div data-testid={DASHBOARD.weekChart} className="bg-white rounded-2xl border border-[#E5E6E3] p-5">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={18} className="text-[#2A4B30]" />
        <h3 className="text-base font-medium text-[#1C1C1A]">Viikon trendi</h3>
        <span className="text-xs text-[#6A6D66] ml-auto">7 päivää · klikkaa pylvästä</span>
      </div>
      <div className="flex items-stretch gap-2 h-44">
        {trend.map((d) => {
          const h = Math.round((d.tilaukset_kg / max) * 100);
          const isToday = d.is_today;
          return (
            <button
              key={d.date}
              type="button"
              onClick={() => onPick(d.date)}
              data-testid={`${DASHBOARD.weekBarPrefix}${d.date}`}
              className="flex-1 flex flex-col items-center group cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2A4B30] focus:ring-offset-1 rounded"
              title={`Avaa ${format(parseISO(d.date), "EEEE d.M.", { locale: fi })} (${d.tilaukset_kg > 0 ? fmtKg(d.tilaukset_kg) : 'ei tilauksia'})`}
            >
              <div className="h-4 text-[11px] tabular-nums text-[#6A6D66] opacity-0 group-hover:opacity-100 transition-opacity">
                {d.tilaukset_kg > 0 ? fmtKg(d.tilaukset_kg) : ''}
              </div>
              <div className="w-full flex-1 flex items-end justify-center px-1">
                <div
                  className={`w-full rounded-t-md transition-all group-hover:scale-y-105 origin-bottom ${
                    isToday ? 'bg-[#C05A3C]' : 'bg-[#2A4B30]'
                  } ${d.tilaukset_kg === 0 ? 'opacity-20 group-hover:opacity-40' : 'opacity-90 group-hover:opacity-100'}`}
                  style={{ height: `${Math.max(h, d.tilaukset_kg > 0 ? 8 : 4)}%` }}
                />
              </div>
              <div className={`mt-1.5 text-[11px] text-center ${isToday ? 'font-semibold text-[#C05A3C]' : 'text-[#6A6D66]'}`}>
                {fmtDay(d.date)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TopCustomers({ items, onPickCustomer }) {
  const max = Math.max(1, ...items.map((c) => c.total_kg));
  return (
    <div data-testid={DASHBOARD.topCustomers} className="bg-white rounded-2xl border border-[#E5E6E3] p-5">
      <div className="flex items-center gap-2 mb-4">
        <Award size={18} className="text-[#C05A3C]" />
        <h3 className="text-base font-medium text-[#1C1C1A]">Top asiakkaat</h3>
        <span className="text-xs text-[#6A6D66] ml-auto">viim. 30 päivää · klikkaa nimeä</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#6A6D66] py-8 text-center">Ei tilauksia viim. 30 päivänä</div>
      ) : (
        <ul className="space-y-2">
          {items.map((c, i) => {
            const w = Math.round((c.total_kg / max) * 100);
            return (
              <li key={c.customer_id}>
                <button
                  type="button"
                  onClick={() => onPickCustomer(c)}
                  data-testid={`${DASHBOARD.topCustomerRowPrefix}${c.customer_id}`}
                  className="relative w-full rounded-lg overflow-hidden bg-[#F9F9F6] hover:bg-[#F4F0E2] focus:outline-none focus:ring-2 focus:ring-[#C05A3C] focus:ring-offset-1 transition-colors text-left cursor-pointer group"
                  title={`Avaa ${c.asiakas} -tilaushistoria`}
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-[#E9D9B5]/60 group-hover:bg-[#E9D9B5]/90 transition-colors"
                    style={{ width: `${w}%` }}
                  />
                  <div className="relative px-3 py-2 flex items-center gap-3">
                    <span className={`tabular-nums text-xs w-5 ${i < 3 ? 'font-semibold text-[#C05A3C]' : 'text-[#6A6D66]'}`}>
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-[#1C1C1A] truncate">{c.asiakas}</div>
                      <div className="text-xs text-[#6A6D66] truncate">{c.ketju} · {c.ryhma}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold tabular-nums text-[#2A4B30]">{fmtKg(c.total_kg)}</div>
                      <div className="text-[11px] text-[#6A6D66]">{c.orders} tilausta</div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function RoutesToday({ items }) {
  return (
    <div data-testid={DASHBOARD.routesToday} className="bg-white rounded-2xl border border-[#E5E6E3] p-5">
      <div className="flex items-center gap-2 mb-4">
        <Truck size={18} className="text-[#2A4B30]" />
        <h3 className="text-base font-medium text-[#1C1C1A]">Päivän reitit</h3>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-[#6A6D66] py-8 text-center">Ei tilauksia tänään</div>
      ) : (
        <ul className="divide-y divide-[#E5E6E3]">
          {items.map((r) => (
            <li
              key={r.route}
              data-testid={`${DASHBOARD.routeRowPrefix}${r.route}`}
              className="py-2.5 flex items-center justify-between"
            >
              <div>
                <div className="text-sm font-medium text-[#1C1C1A]">{r.route}</div>
                <div className="text-xs text-[#6A6D66]">{r.customer_count} asiakasta</div>
              </div>
              <div className="text-sm font-semibold tabular-nums text-[#2A4B30]">{fmtKg(r.total_kg)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function CustomerHistoryDialog({ customer, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/customers/${customer.customer_id}/recent-orders?days=60`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) toast.error('Tilaushistorian lataus epäonnistui'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [customer]);

  if (!customer) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dashboard-customer-history-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl font-heading flex items-center gap-2">
            <Award size={20} className="text-[#C05A3C]" />
            {customer.asiakas}
          </DialogTitle>
          <DialogDescription>
            {customer.ketju} · {customer.ryhma} — viim. 60 päivän tilaushistoria
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-sm text-[#6A6D66]">Ladataan…</div>
        ) : !data || data.orders.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#6A6D66]">Ei tilauksia viim. 60 päivänä</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between px-3 py-2 bg-[#F4F6F3] rounded-lg">
              <div className="text-xs uppercase tracking-wider text-[#6A6D66]">Yhteensä</div>
              <div className="text-lg font-semibold tabular-nums text-[#2A4B30]">{fmtKg(data.total_kg)}</div>
            </div>
            <div className="overflow-x-auto rounded-lg border border-[#E5E6E3]">
              <table className="w-full text-sm" data-testid="dashboard-customer-history-table">
                <thead className="bg-[#F9F9F6] text-xs uppercase tracking-wider text-[#6A6D66]">
                  <tr>
                    <th className="text-left px-3 py-2">Päivä</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Lähete</th>
                    <th className="text-right px-3 py-2">Määrä</th>
                  </tr>
                </thead>
                <tbody>
                  {data.orders.map((o) => (
                    <tr key={o.id} className="border-t border-[#E5E6E3] hover:bg-[#F9F9F6]">
                      <td className="px-3 py-1.5">
                        <Link
                          to={`/tilaukset?date=${o.date}`}
                          onClick={onClose}
                          className="text-[#2A4B30] hover:underline"
                        >
                          {format(parseISO(o.date), "d.M.yyyy", { locale: fi })}
                          <span className="text-[10px] text-[#6A6D66] ml-1 capitalize">
                            ({format(parseISO(o.date), "EEE", { locale: fi })})
                          </span>
                        </Link>
                      </td>
                      <td className="px-3 py-1.5">
                        {o.status ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            o.status === 'Arvio' ? 'bg-[#FDF6EB] text-[#9B6B1A]' :
                            o.status === 'Laskutettu' ? 'bg-[#E8F0E9] text-[#2A4B30]' :
                            'bg-[#F4F6F3] text-[#1C1C1A]'
                          }`}>{o.status}</span>
                        ) : <span className="text-[#C2C2BE]">—</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs text-[#6A6D66]">{o.lahete_no || '—'}</td>
                      <td className="px-3 py-1.5 text-right font-mono tabular-nums">{fmtKg(o.maara_kg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const navigate = useNavigate();

  const openDay = (dateIso) => navigate(`/tilaukset?date=${dateIso}`);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const r = await api.get('/dashboard/summary');
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error('Etusivun lataus epäonnistui');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    // Refresh every 60 seconds for live-feeling dashboard
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  if (loading || !data) {
    return (
      <div data-testid={DASHBOARD.page} className="space-y-6">
        <div className="h-8 w-48 bg-[#E5E6E3] rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[0,1,2,3].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-white border border-[#E5E6E3] animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const t = data.today;
  const saldoNeg = (t.saldo_kg || 0) < 0;
  const todayKey = data.date;

  return (
    <div data-testid={DASHBOARD.page} className="space-y-6">
      {/* Hero header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#6A6D66]">
            <Sprout size={14} className="text-[#2A4B30]" />
            Tammilehdon Herneet
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight mt-1">
            Etusivu — {format(parseISO(todayKey), "d.M.yyyy", { locale: fi })}
          </h1>
          <div className="text-sm text-[#6A6D66] mt-1 capitalize">
            {format(parseISO(todayKey), 'EEEE', { locale: fi })}
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/tilaukset"
            data-testid={DASHBOARD.openTodayBtn}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#2A4B30] text-white text-sm hover:bg-[#1f3a25] transition-colors"
          >
            <Calendar size={15} /> Avaa päivän tilaukset <ArrowRight size={14} />
          </Link>
          <Link
            to="/laskutus"
            data-testid={DASHBOARD.openLaskutusBtn}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2A4B30] text-[#2A4B30] text-sm hover:bg-[#E9D9B5]/40 transition-colors"
          >
            Laskutukseen <ArrowRight size={14} />
          </Link>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          testid={DASHBOARD.kpiOrders}
          icon={Package}
          label="Päivän tilaukset"
          value={fmtKg(t.tilaukset_kg)}
          hint={`${t.order_count} tilausta · ${t.customer_count} asiakasta`}
          tone="green"
        />
        <KpiCard
          testid={DASHBOARD.kpiStock}
          icon={ShoppingBag}
          label="Varastossa + noukittu"
          value={fmtKg((t.varastossa_kg || 0) + (t.noukittu_kg || 0))}
          hint={`${fmtKg(t.varastossa_kg)} + ${fmtKg(t.noukittu_kg)}`}
          tone="sand"
        />
        <KpiCard
          testid={DASHBOARD.kpiSaldo}
          icon={Scale}
          label="Saldo"
          value={fmtKg(t.saldo_kg)}
          hint={saldoNeg ? 'Vajaus — tarkista varasto' : 'Riittää tilauksiin'}
          tone={saldoNeg ? 'rust' : 'cream'}
        />
        <KpiCard
          testid={DASHBOARD.kpiOrderCount}
          icon={AlertCircle}
          label="Avoimet tilaukset"
          value={`${t.order_count}`}
          hint={t.order_count > 0 ? 'klikkaa "Avaa päivän tilaukset"' : 'ei avoimia tänään'}
          tone="cream"
        />
      </div>

      {/* Middle: week trend + top customers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WeekChart trend={data.week_trend} onPick={openDay} />
        <TopCustomers items={data.top_customers} onPickCustomer={setSelectedCustomer} />
      </div>

      {/* Bottom: routes today */}
      <RoutesToday items={data.routes_today} />

      {/* Customer history dialog (mounted only when a customer is picked) */}
      <CustomerHistoryDialog customer={selectedCustomer} onClose={() => setSelectedCustomer(null)} />
    </div>
  );
}
